// @ts-strict-ignore
import { articleAttributesNL } from './article-attributes-nl'
import * as S from './elements'
import { getDescription } from './helpers/get-description'
import { Container, Text } from '@gassan-ui'
import { Article } from '@generated'
import { TabPanels, Tabs } from '@reach/tabs'
import { useTranslation } from 'next-i18next'
import { FC, useEffect, useRef, useState } from 'react'

export type LineProps = { left: number; width: number }

const excludedSpecs = ['priceCategory', 'Delivery', 'isMemberonlyDiscount']

type Props = {
  article: Article
  brandDescription: string | null
  productDescription: string | null
}

const ProductTabs: FC<Props> = ({ article, brandDescription, productDescription }) => {
  const tabParent = useRef<HTMLDivElement>()
  const [linePos, setLinePos] = useState<LineProps>({ left: 0, width: 0 })
  const {
    t,
    i18n: { language },
  } = useTranslation(['product-detail', 'other'])
  const isPreOwned = article.webID === 'gpcpreown'
  const description = getDescription({
    brandDescription,
    productDescription,
    isPreOwned,
  })

  useEffect(() => {
    setIndex(0)
  }, [])

  const setIndex = (index: number) => {
    setTimeout(() => {
      const activeTab = tabParent?.current?.children[index] as HTMLElement
      activeTab &&
        setLinePos({
          left: activeTab.offsetLeft,
          width: activeTab.offsetWidth,
        })
    }, 20)
  }

  let aboutTitle = ''
  switch (article.articleTypeID) {
    case 1:
    case 5:
      aboutTitle = t('aboutWatch', { ns: 'product-detail' })
      break
    case 4:
      aboutTitle = t('aboutAccessory', { ns: 'product-detail' })
      break
    default:
      aboutTitle = t('aboutJewelry', { ns: 'product-detail' })
  }

  return (
    <Container mt={{ _: '2.5rem', small: '4rem', large: '3rem' }}>
      <Tabs onChange={(index) => setIndex(index)}>
        <S.TabList ref={tabParent}>
          {article.articleAttributes && (
            <S.Tab>{t('specifications', { ns: 'product-detail' })}</S.Tab>
          )}
          <S.Tab>{aboutTitle}</S.Tab>
          <S.AnimatedLine left={linePos.left} width={linePos.width} />
        </S.TabList>
        <TabPanels>
          <S.TabPanel>
            <S.ContentBox columns={article.articleAttributes.length > 8 ? 2 : 1}>
              <S.Specification>
                <Text mb="0" color="shades.500" width={['8rem', '8rem', '8rem', '12rem']}>
                  {t('referenceNumber', { ns: 'other' })}
                </Text>
                <Text mb="0" width={['8rem', '8rem', '8rem', '12rem']}>
                  {article.supplierNr}
                </Text>
              </S.Specification>
              {article.collection && article.collection.length && (
                <S.Specification>
                  <Text mb="0" color="shades.500" width={['8rem', '8rem', '8rem', '12rem']}>
                    {t('collection', { ns: 'other' })}
                  </Text>
                  <Text mb="0" width={['8rem', '8rem', '8rem', '12rem']}>
                    {article.collection}
                  </Text>
                </S.Specification>
              )}
              {article.modelName && article.modelName.length && (
                <S.Specification>
                  <Text mb="0" color="shades.500" width={['8rem', '8rem', '8rem', '12rem']}>
                    {t('modelName', { ns: 'other' })}
                  </Text>
                  <Text mb="0" width={['8rem', '8rem', '8rem', '12rem']}>
                    {article.modelName}
                  </Text>
                </S.Specification>
              )}
              {article.articleAttributes
                .filter(
                  (attribute) =>
                    !excludedSpecs.includes(attribute.attributeName) &&
                    attribute.attributeValue.length,
                )
                .map((attribute, key) => (
                  <S.Specification key={key}>
                    <Text mb="0" color="shades.500" width={['8rem', '8rem', '8rem', '12rem']}>
                      {language === 'nl'
                        ? articleAttributesNL[attribute.attributeOriginalName] ||
                          attribute.attributeName
                        : attribute.attributeOriginalName}
                    </Text>
                    <Text mb="0" width={['8rem', '8rem', '8rem', '12rem']}>
                      {attribute.attributeValue}
                    </Text>
                  </S.Specification>
                ))}
            </S.ContentBox>
          </S.TabPanel>
          <S.TabPanel>
            <S.ContentBox columns={description && description.length > 600 ? 2 : 1}>
              {description && (
                <Text
                  style={{ whiteSpace: 'pre-line' }}
                  dangerouslySetInnerHTML={{ __html: description }}
                ></Text>
              )}
            </S.ContentBox>
          </S.TabPanel>
        </TabPanels>
      </Tabs>
    </Container>
  )
}

export default ProductTabs
